import { useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { annotation as csAnnotation } from '@cornerstonejs/tools';
import {
  selectAnnotation,
  selectSelectedAnnotationUID,
} from '@/state/slices/measurementSession';
import type { RootState } from '@/state/store';

interface UseDeselectOnFrameLeaveArgs {
  dicomUuid: string | undefined;
  isVideoViewport: boolean;
  /** The pane's displayed frame (0-based; annotation imageIds are 1-based). */
  currentFrame: number;
}

/**
 * Selection means "a visible trace" (AP-764 I7 turn-off rules): when this pane
 * navigates away from the selected annotation's frame — scrub, arrow keys, or
 * cine playback advancing — the selection clears, in the worksheet and the
 * viewer alike. Video panes only; stills have no frames to leave.
 *
 * Reacts ONLY to frame changes (prev-frame ref guard), and deselects ONLY
 * when the pane was ON the annotation's frame and moved off it. "Leaving"
 * requires having been there: a worksheet label click dispatches the selection
 * and THEN navigates the pane, and the navigation's own transition frames
 * (dicom load, seek landing, frame-index jitter after a seek) are frame
 * changes that never visited the annotation's frame — they must not clear the
 * selection they are routing toward.
 */
export function useDeselectOnFrameLeave({
  dicomUuid,
  isVideoViewport,
  currentFrame,
}: UseDeselectOnFrameLeaveArgs): void {
  const dispatch = useDispatch();
  // Read the selection imperatively — subscribing would add it to the effect's
  // reactivity and break the "frame changes only" contract above.
  const store = useStore<RootState>();
  const prevFrameRef = useRef(currentFrame);

  useEffect(() => {
    const prevFrame = prevFrameRef.current;
    prevFrameRef.current = currentFrame;
    if (!isVideoViewport || !dicomUuid) return;
    if (prevFrame === currentFrame) return;

    const selectedUID = selectSelectedAnnotationUID(store.getState());
    if (!selectedUID) return;

    const ann = csAnnotation.state.getAnnotation(selectedUID) as
      | { metadata?: { referencedImageId?: string } }
      | undefined;
    const refImageId = ann?.metadata?.referencedImageId;
    // Only this pane's video annotations — another pane's selection (or a
    // still's) is not ours to clear.
    if (!refImageId?.startsWith(`video:${dicomUuid}/frames/`)) return;

    const annotationFrame = Number(refImageId.match(/frames\/(\d+)$/)?.[1]);
    if (!Number.isFinite(annotationFrame)) return;

    // Annotation imageIds are 1-based (frameNumber = currentFrame + 1).
    // Deselect only on a WAS-on → moved-off transition: prev frame matched the
    // annotation, the new one doesn't. Transition frames during navigation
    // never matched, so they pass through untouched.
    if (
      prevFrame + 1 === annotationFrame &&
      currentFrame + 1 !== annotationFrame
    ) {
      dispatch(selectAnnotation(null));
    }
  }, [currentFrame, dicomUuid, isVideoViewport, dispatch, store]);
}
